import { useMemo, useState } from "react";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { PublicEvent, PanchangDayMarkers } from "@vedaunion/shared";
import {
  usePanchangStatus,
  usePanchangRange,
  usePanchangDay,
} from "../../lib/panchang";
import { MonthGrid } from "./MonthGrid";
import { PanchangDayPanel } from "./PanchangDayPanel";
import { LocationPicker, useLocationPref } from "./LocationPicker";
import { FullMoonIcon, NewMoonIcon } from "./lunarIcons";

interface CalendarViewProps {
  register: "rich" | "functional";
  cursor: Date;
  onCursorChange: (d: Date) => void;
  events: PublicEvent[];
  basePath: string;
  /** Whether to show the MonthGrid's own month/nav header (pass false when the
   *  parent already renders a nav row, e.g. the dashboard controls bar). */
  showHeader?: boolean;
  /** Optional: called when user clicks an empty day cell to create an event. */
  onCreateAt?: (when: Date) => void;
}

export function CalendarView({
  register,
  cursor,
  onCursorChange,
  events,
  basePath,
  showHeader = true,
  onCreateAt,
}: CalendarViewProps) {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  // The single info panel below the grid always targets a day; today by default.
  const [selectedDay, setSelectedDay] = useState<string>(todayStr);

  const { location, setLocation } = useLocationPref();

  // Date range that the month grid shows (including leading/trailing week days).
  const from = format(startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), "yyyy-MM-dd");
  const to = format(addDays(endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }), 1), "yyyy-MM-dd");

  const statusQ = usePanchangStatus();
  const enabled = statusQ.data?.enabled ?? false;
  const timezone = statusQ.data?.timezone ?? "Europe/Ljubljana";

  const rangeQ = usePanchangRange(from, to, enabled, location);
  const dayQ = usePanchangDay(selectedDay, enabled, location);

  const panchangMap = useMemo(() => {
    const m = new Map<string, PanchangDayMarkers>();
    for (const d of rangeQ.data?.days ?? []) m.set(d.date, d);
    return m;
  }, [rangeQ.data]);

  const selectedLabel = format(new Date(`${selectedDay}T00:00:00`), "EEEE, d MMMM yyyy");
  // Selecting a day always targets it (never an empty state). Tapping the
  // already-selected day falls back to today.
  const handleSelectDay = (dayKey: string) => {
    setSelectedDay((prev) => (prev === dayKey ? todayStr : dayKey));
  };

  const locationName = location?.place ?? statusQ.data?.locationName ?? "Krnjak";

  return (
    <div className="cal-view">
      {/* Meta row above the grid: location picker (+ legend in rich register). */}
      {enabled && (
        <div className="cal-view-meta">
          {register === "rich" ? (
            <p className="cal-pa-legend">
              Tap any day for its full panchāṅga.{" "}
              <FullMoonIcon size={11} className="cal-legend-icon" /> Pūrṇimā ·{" "}
              <NewMoonIcon size={11} className="cal-legend-icon" /> Amāvāsyā · dots mark Ekādaśī, festivals.
            </p>
          ) : (
            <p className="pa-eyebrow" style={{ margin: 0 }}>
              Vedic calendar · timings for {locationName}
            </p>
          )}
          <LocationPicker location={location} onLocationChange={setLocation} />
        </div>
      )}

      {/* Calendar grid — the primary widget. */}
      <MonthGrid
        cursor={cursor}
        events={events}
        onCursorChange={onCursorChange}
        basePath={basePath}
        panchang={enabled ? panchangMap : undefined}
        onSelectDay={enabled ? handleSelectDay : undefined}
        selectedDay={enabled ? selectedDay : undefined}
        showHeader={showHeader}
        onCreateAt={onCreateAt}
      />

      {/* Single info panel below the grid for the selected day (today by
          default). The live "right now" strip shows only when today is selected. */}
      {enabled && dayQ.data?.day && (
        <div className={`cal-view-day-panel${register === "functional" ? " pa-day-sheet" : ""}`}>
          <PanchangDayPanel
            day={dayQ.data.day}
            timezone={timezone}
            dateLabel={selectedLabel}
            showNow={selectedDay === todayStr}
          />
        </div>
      )}
    </div>
  );
}
